import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from 'react-router-dom';
import { useGuardianContext } from "../../context/GuardianContext";
import { GuardianForm } from "./";
import { AthleticFormGuardianSignatureRequirement } from "../../models/api-models/guardians/guardian-form-submission";
import { GuardianLayout } from "./Layout/GuardianLayout";
import { GuardiansList } from "./List";
import { GuardianApi } from "../../api/GuardianApi";
import { ToastService } from "../../services/ToastService";
import { HttpUtility } from "../../utilities/HttpUtility";
import { MediaApi } from "../../api/MediaApi";

export const GuardianContainer: FC = () => {
  const { submissionId, guardianId } = useParams();
  const history = useHistory();
  const isMountedRef = useRef(true);

  const {
    form,
    formSubmission,
    setData,
    signature,
    setIsLoad,
    partialUpdateFormSubmission,
  } = useGuardianContext();

  const currentGuardianIsActive = useMemo(() => formSubmission && formSubmission.guardians.some(g => g.id === guardianId && g.isActive), [formSubmission]);
  
  const navigateToAccessDenied = useCallback(() => {
    history.push('/guardians/access-denied', {
        title: 'Access Denied',
        message: 'You have been removed from the guardians list. You no longer have permission to sign this form.'
      });
  }, [history])

  const loadData = useCallback(async () => {
    try {
      if (!isMountedRef.current) {
        return
      };
      if (isMountedRef.current) { 
        setIsLoad(true); 
      }
      const formResult = await GuardianApi.getForm(submissionId, guardianId);
      const formSubmissionResult = await GuardianApi.getFormSubmission(
        submissionId,
        guardianId
      );
      const formData = JSON.parse(formSubmissionResult.formDataJson);
      const data = { ...formSubmissionResult, formData };
      if(isMountedRef.current) {
        setData({
          form: { ...formResult, fields: formResult.fields.sort((a, b) => a.order - b.order)},
          submission: data,
        });
      }
    } catch (e) {
      navigateToAccessDenied();
    } finally {
      if (isMountedRef.current) {
        setIsLoad(false);
      }
    }
  }, [navigateToAccessDenied]);

  useEffect(() => {
    isMountedRef.current = true;
    loadData();
    return () => {
      isMountedRef.current = false;
    }
  }, []);

  useEffect(() => {
    if(formSubmission && formSubmission.guardians && !currentGuardianIsActive) {
      navigateToAccessDenied();
    }
  }, [formSubmission, currentGuardianIsActive]);

  const onScrollToSignature = useCallback(() => {
    const element = document.getElementById("guardian-signature");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }, []);


  const submit = async () => {
    if (!signature) {
      ToastService.error(
        "Guardian signature required before confirmation.",
        onScrollToSignature
      );
      return;
    }
    setIsLoad(true);
    const file = HttpUtility.dataURLToBlob(signature);
    const fileName = "signature.png";
    const formData = new FormData();
    formData.append("file", file, fileName);
    try {
      const { id: signatureMediaId } = await MediaApi.upload(
        formData,
        fileName,
        false
      );
      await GuardianApi.signForm({
        signatureMediaId,
        submissionId,
        guardianId,
      });
      const currentGuardianIndex = formSubmission.guardians.findIndex(
        (g) => g.id === guardianId
      );
      const updatedGuardian = {
        ...formSubmission.guardians[currentGuardianIndex],
        signatureMediaId,
        signatureDate: new Date(),
      };
      const guardians = formSubmission.guardians.splice(
        currentGuardianIndex,
        1,
        updatedGuardian
      );
      partialUpdateFormSubmission({ guardians });
      ToastService.sucess('You have successfully signed the form.')
    } finally {
      setIsLoad(false);
    }
  };

  const isCurrentGuardianSinged =
    formSubmission && typeof formSubmission.guardians !== "undefined"
      ? formSubmission.guardians.some(
          (g) =>
            g.id === guardianId && typeof g.signatureMediaId !== "undefined"
        )
      : false;

  const signatureRequirement =
    formSubmission &&
    typeof formSubmission.guardianSignatureRequirement !== "undefined"
      ? formSubmission.guardianSignatureRequirement
      : AthleticFormGuardianSignatureRequirement.None;

  const guardians =
    formSubmission && formSubmission.guardians ? formSubmission.guardians : [];

  const isOtherGuardiansSigned = formSubmission && formSubmission.guardians.some(g => g.signatureMediaId && g.id !== guardianId && g.isActive) && signatureRequirement === AthleticFormGuardianSignatureRequirement.OneGuardian;

  return (
    <>
      {form && formSubmission && (
        <GuardianLayout
          formTitle={form.name}
          submit={submit}
          isCurrentGuardianSinged={isCurrentGuardianSinged}
          isOtherGuardiansSigned={isOtherGuardiansSigned}
          currentGuardianIsActive={currentGuardianIsActive}
        >
          <div className="guardian-container">
            <div style={{ overflowY: "auto", width: "100%" }}>
              <div
                style={{
                  padding: "16px 0",
                  width: "60%",
                  margin: "0px 80px 0 auto",
                }}
              >
                <GuardianForm 
                  form={form} 
                  formSubmission={formSubmission}
                  isCurrentGuardianSinged={isCurrentGuardianSinged}
                  isOtherGuardiansSigned={isOtherGuardiansSigned}
                  currentGuardianIsActive={currentGuardianIsActive}
                  />
              </div>
            </div>
            <GuardiansList
              isCurrentGuardianSinged={isCurrentGuardianSinged}
              requirement={signatureRequirement}
              guardians={guardians}
              isOtherGuardiansSigned={isOtherGuardiansSigned}
              currentGuardianIsActive={currentGuardianIsActive}
            />
          </div>
        </GuardianLayout>
      )}
    </>
  );
};
